import React, { useState } from "react";
import FeatherIcon from "feather-icons-react";
import { isTerminalBulkUnmatchStatus } from "./hooks/useBulkUnmatch";
import { bulkUnmatchTargetNoun } from "./utils/bulkUnmatchLabels";

/**
 * Barra flotante de avance de una acción de conciliación masiva.
 *
 * Es un elemento propio y no un toast porque el proceso puede durar minutos: un
 * toast que se auto-cierra dejaría al usuario sin saber si sigue corriendo. Al
 * terminar se queda con el resumen (y el detalle de errores) hasta que lo cierren.
 *
 * Se reutiliza para deshacer matches y para conciliar traspaso entre cuentas.
 */
export default function BulkUnmatchProgress({
  job,
  percent,
  onCancel,
  onRetry,
  onClose,
  title = "Deshaciendo matches",
  icon,
}) {
  const [showFailures, setShowFailures] = useState(false);
  if (!job) return null;

  const totals = job.totals || {};
  const done = isTerminalBulkUnmatchStatus(job.status);
  const failed = Number(totals.failed || 0);
  const failures = job.failures || [];
  const resolvedIcon = done
    ? (failed > 0 ? "alert-triangle" : "check-circle")
    : (icon || "unlink");

  const barColor =
    job.status === "FAILED"
      ? "tw-bg-red-500"
      : job.status === "PARTIAL" || job.status === "CANCELLED"
        ? "tw-bg-amber-500"
        : done
          ? "tw-bg-emerald-500"
          : "tw-bg-sky-500";

  return (
    <section
      className="tw-fixed tw-bottom-4 tw-right-4 tw-z-[9999] tw-w-[360px] tw-max-w-[92vw] tw-rounded-lg tw-bg-gray-800 tw-p-3 tw-text-gray-100 tw-shadow-lg"
      role="status"
      aria-live="polite"
    >
      <header className="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium">
          <FeatherIcon icon={resolvedIcon} size={15} />
          {title}
        </span>
        {done && (
          <button
            type="button"
            className="tw-border-none tw-bg-transparent tw-p-0 tw-text-gray-300"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FeatherIcon icon="x" size={14} />
          </button>
        )}
      </header>

      <div className="tw-mt-2 tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded tw-bg-gray-600">
        <div
          className={`tw-h-full tw-transition-all tw-duration-500 ${barColor}`}
          style={{ width: `${done ? 100 : Math.max(percent || 0, 2)}%` }}
        />
      </div>

      <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-gray-300">
        {job.message ||
          `${totals.targetsProcessed || 0} de ${totals.targets || 0} ${bulkUnmatchTargetNoun(
            job.type,
            totals.targets,
          )} procesado(s)…`}
      </p>

      {done && (totals.entriesDeleted > 0 || totals.obligationsDeleted > 0 || totals.skipped > 0) && (
        <p className="tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-gray-400">
          {[
            totals.entriesDeleted > 0 && `${totals.entriesDeleted} asiento(s) eliminado(s)`,
            totals.obligationsDeleted > 0 && `${totals.obligationsDeleted} obligación(es) eliminada(s)`,
            totals.skipped > 0 && `${totals.skipped} ya estaba(n) sin match`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      <footer className="tw-mt-2 tw-flex tw-items-center tw-gap-3 tw-text-xs">
        {!done && onCancel && (
          <button
            type="button"
            className="tw-border-none tw-bg-transparent tw-p-0 tw-text-gray-300 tw-underline"
            onClick={onCancel}
            disabled={job.cancelRequested}
          >
            {job.cancelRequested ? "Cancelando…" : "Cancelar"}
          </button>
        )}

        {done && failed > 0 && (
          <>
            <button
              type="button"
              className="tw-border-none tw-bg-transparent tw-p-0 tw-text-gray-300 tw-underline"
              onClick={() => setShowFailures((v) => !v)}
            >
              {showFailures ? "Ocultar detalle" : `Ver detalle (${failed})`}
            </button>
            {onRetry && (
              <button
                type="button"
                className="tw-border-none tw-bg-transparent tw-p-0 tw-text-gray-300 tw-underline"
                onClick={onRetry}
              >
                Reintentar lo fallido
              </button>
            )}
          </>
        )}
      </footer>

      {showFailures && failures.length > 0 && (
        <ul className="tw-mt-2 tw-mb-0 tw-max-h-40 tw-list-none tw-overflow-y-auto tw-p-0 tw-text-[11px] tw-text-gray-300">
          {failures.map((failure, index) => (
            <li key={`${failure.targetId}-${failure.counterpartId}-${index}`} className="tw-mb-1">
              <span className="tw-font-mono tw-text-gray-400">
                {String(failure.targetId).slice(-6)}
              </span>{" "}
              {failure.message}
            </li>
          ))}
          {job.failuresTruncated && (
            <li className="tw-italic tw-text-gray-400">
              (solo se guardan los primeros errores; los contadores sí son exactos)
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
